import { useMemo } from "react";

import { SMTEntryType, SMTFallbackUIConfig } from "@/constants/SMT";
import { ModalTestID } from "@/constants/TestID";
import type { AddMealRequestBody } from "@/store/api/meals";
import type { SMTScreenPath } from "@/ui/components/smt/AddSMTEntry/AddSMTEntryFlow";
import AddFoodContainer from "@/ui/components/smt/food/AddFoodContainer";
import BottomSheetModal from "@/ui/composed/modal/BottomSheetModal";
import { FAIconName, Icon } from "@/ui/core";
import { ColorToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

const baseTestID = joinTestIDs(ModalTestID.SelfMonitoring, "add-entry");

interface AddSMTEntryBottomSheetModalProps {
  entryType: SMTEntryType;
  isOpen: boolean;
  onDismiss: () => void;
  onSaveEntry: (params: AddMealRequestBody) => Promise<void>;
  patientExternalId: string;
  screenPath: SMTScreenPath;
  uiConfig?: SMTEntryFormUIConfig;
}

const AddSMTEntryBottomSheetModal = ({
  entryType,
  isOpen,
  onDismiss,
  onSaveEntry,
  patientExternalId,
  screenPath,
  uiConfig = SMTFallbackUIConfig.addFood.entryForm,
}: AddSMTEntryBottomSheetModalProps) => {
  const config = useMemo(() => {
    // eslint-disable-next-line sonarjs/no-small-switch -- setting this up early for use to extend with additional entry types later
    switch (entryType) {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- setting this up early for use to extend with additional entry types later
      case SMTEntryType.Meal:
        return {
          children: (
            <AddFoodContainer
              onSaveEntry={onSaveEntry}
              patientExternalId={patientExternalId}
              screenPath={screenPath}
              uiConfig={uiConfig}
            />
          ),
          icon: (
            <Icon
              color={ColorToken.AccentPeriwinkle}
              name={FAIconName.Salad}
              size="lg"
              testID={joinTestIDs(baseTestID, "title")}
            />
          ),
          title: uiConfig.title,
        };
    }
  }, [entryType, screenPath, uiConfig, onSaveEntry, patientExternalId]);

  return (
    <BottomSheetModal
      dismissAccessibilityLabel="Dismiss Add Entry Modal"
      icon={config.icon}
      isCloseOnOverlayPressDisabled
      isOpen={isOpen}
      isSwipeDownToCloseDisabled
      onRequestClose={onDismiss}
      shouldResizeOnContentChange
      testID={baseTestID}
      title={config.title}
    >
      {config.children}
    </BottomSheetModal>
  );
};

export type { AddSMTEntryBottomSheetModalProps };
export default AddSMTEntryBottomSheetModal;
